import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "../../lib/api";
import { Loader2, Download, MapPin, KeyRound, Mail } from "lucide-react";
import { PageHeader, fmtDateTime, Empty } from "../../components/admin/ui";

const Members = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | portal | updates

  useEffect(() => {
    api.get("/admin/members").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const portal = items.filter((m) => m.in_portal).length;
    return { total: items.length, portal, updates: items.length - portal };
  }, [items]);

  const filtered = items.filter((m) => {
    if (filter === "portal" && !m.in_portal) return false;
    if (filter === "updates" && m.in_portal) return false;
    if (!q) return true;
    const v = q.toLowerCase();
    return (m.first_name || "").toLowerCase().includes(v) ||
           (m.last_name || "").toLowerCase().includes(v) ||
           (m.email || "").toLowerCase().includes(v) ||
           (m.city_state || "").toLowerCase().includes(v);
  });

  return (
    <div className="space-y-8" data-testid="admin-members">
      <PageHeader
        overline="Members"
        title={<>Connected to the <span className="italic gold-text">fire</span></>}
        subtitle="Every soul who's opted in — public updates list and Member Portal sign-ins."
      >
        <a href={`${API}/admin/members/export.csv`} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2.5 !px-4 !text-sm" data-testid="export-members-csv"><Download className="h-3.5 w-3.5" /> Export CSV</a>
      </PageHeader>

      <section className="grid grid-cols-3 gap-3" data-testid="members-stats">
        {[
          { label: "All members", value: stats.total, key: "all" },
          { label: "Member Portal", value: stats.portal, key: "portal" },
          { label: "Updates list", value: stats.updates, key: "updates" },
        ].map((s) => (
          <button key={s.key} onClick={() => setFilter(s.key)} className={`bento-card !p-5 text-left transition ${filter === s.key ? "ring-1 ring-[#C9A44C]/60" : "opacity-90 hover:opacity-100"}`} data-testid={`members-filter-${s.key}`}>
            <span className="glow-blob" />
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">{s.label}</div>
            <div className="text-3xl font-light text-[#F6F0E6] mt-2">{s.value}</div>
          </button>
        ))}
      </section>

      <section className="bento-card !p-5">
        <span className="glow-blob" />
        <input className="bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-full px-4 py-2 text-sm text-[#F6F0E6] focus:outline-none w-full" placeholder="Search by name, email, or location…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="members-search" />
      </section>

      {loading ? (
        <div className="text-[#D9D9D9]/60"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <Empty title="No members yet" subtitle="As people sign up for updates or join the Member Portal, they appear here." />
      ) : (
        <div className="bento-card !p-0 overflow-hidden">
          <span className="glow-blob" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.22em] uppercase text-[#C9A44C] border-b border-[#C9A44C]/15">
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4 hidden md:table-cell">Email</th>
                  <th className="px-6 py-4">Source</th>
                  <th className="px-6 py-4 hidden lg:table-cell">Location</th>
                  <th className="px-6 py-4 hidden xl:table-cell">Connection</th>
                  <th className="px-6 py-4">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={`${m.email}-${m.id || ""}`} className="border-b border-[#C9A44C]/8 hover:bg-[#C9A44C]/5" data-testid={`member-row-${m.email}`}>
                    <td className="px-6 py-4 text-[#F6F0E6]">
                      <div className="flex items-center gap-3">
                        {m.picture ? (
                          <img src={m.picture} alt="" className="h-7 w-7 rounded-full object-cover border border-[#C9A44C]/30" />
                        ) : (
                          <div className="h-7 w-7 rounded-full bg-[#C9A44C]/10 border border-[#C9A44C]/20 flex items-center justify-center text-[10px] text-[#C9A44C]">{(m.first_name?.[0] || m.email?.[0] || "?").toUpperCase()}</div>
                        )}
                        <span>{((m.first_name || "") + " " + (m.last_name || "")).trim() || m.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell text-[#D9D9D9]/80">{m.email}</td>
                    <td className="px-6 py-4">
                      {m.in_portal ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase bg-[#C9A44C]/15 text-[#F6E4B0] border border-[#C9A44C]/40" data-testid="badge-portal"><KeyRound className="h-3 w-3" /> Portal</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/5 text-[#D9D9D9]/70 border border-white/10" data-testid="badge-updates"><Mail className="h-3 w-3" /> Updates</span>
                      )}
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell text-[#D9D9D9]/70">{m.city_state && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-[#C9A44C]" />{m.city_state}</span>}</td>
                    <td className="px-6 py-4 hidden xl:table-cell text-[#D9D9D9]/60 truncate max-w-xs">{m.connection || "—"}</td>
                    <td className="px-6 py-4 text-[#D9D9D9] whitespace-nowrap">{fmtDateTime(m.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Members;
